import type { UserAssumptionsDto } from "@penn/web-common";

/**
 * Form fields in CustomizeAssumptions. A blank ("") means "not set" — the user
 * left the field empty, so the backend cost engine should apply its global
 * defaults rather than a hard-coded client value.
 */
export type AssumptionsFormValues = {
  laborRate: number | "";
  workingDaysPerYear: number | "";
  workingHoursPerDay: number | "";
  usefulLifeMachines: number | "";
  scrapManual: number | "";
  scrapAutomated: number | "";
  reworkCost: number | "";
  oeeManual: number | "";
  oeeAutomated: number | "";
};

// C5 (POC parity DEFAULT_ASSUMPTIONS): leave every field BLANK by default so a
// user who never opens this modal never silently locks in a client-side value
// — the backend applies its own global defaults (Calculation Parameters
// dataset) for any field left blank.
const DEFAULT_FORM: AssumptionsFormValues = {
  laborRate: "",
  workingDaysPerYear: "",
  workingHoursPerDay: "",
  usefulLifeMachines: "",
  scrapManual: "",
  scrapAutomated: "",
  reworkCost: "",
  oeeManual: "",
  oeeAutomated: "",
};

function numOr(v: string | undefined, fallback: number | ""): number | "" {
  if (v === undefined || v === "") return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function assumptionsDtoToForm(
  dto: UserAssumptionsDto | null | undefined,
): AssumptionsFormValues {
  if (!dto) return { ...DEFAULT_FORM };
  return {
    laborRate: numOr(dto.laborRate, DEFAULT_FORM.laborRate),
    workingDaysPerYear: numOr(dto.daysPerYear, DEFAULT_FORM.workingDaysPerYear),
    workingHoursPerDay: numOr(dto.hoursPerDay, DEFAULT_FORM.workingHoursPerDay),
    usefulLifeMachines: numOr(
      dto.usefulLifeYears,
      DEFAULT_FORM.usefulLifeMachines,
    ),
    scrapManual: numOr(dto.scrapManual, DEFAULT_FORM.scrapManual),
    scrapAutomated: numOr(dto.scrapAuto, DEFAULT_FORM.scrapAutomated),
    reworkCost: numOr(dto.reworkCost, DEFAULT_FORM.reworkCost),
    oeeManual: numOr(dto.oeeManual, DEFAULT_FORM.oeeManual),
    oeeAutomated: numOr(dto.oeeAuto, DEFAULT_FORM.oeeAutomated),
  };
}

export function formToAssumptionsDto(
  form: AssumptionsFormValues,
): UserAssumptionsDto {
  // A blank field is sent as "" so svc-03 stores NULL and the cost engine applies
  // its global default instead of a client-side value (C5).
  const str = (v: number | "") => (v === "" ? "" : String(v));
  return {
    laborRate: str(form.laborRate),
    daysPerYear: str(form.workingDaysPerYear),
    hoursPerDay: str(form.workingHoursPerDay),
    usefulLifeYears: str(form.usefulLifeMachines),
    scrapManual: str(form.scrapManual),
    scrapAuto: str(form.scrapAutomated),
    reworkCost: str(form.reworkCost),
    oeeManual: str(form.oeeManual),
    oeeAuto: str(form.oeeAutomated),
  };
}
